import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
    title = 'AircoreDynamics - Aviation & Aerospace News, Research & Analysis',
    description = 'Breaking aviation news, in-depth research articles, and expert analysis on aerospace technology, space exploration, and flight dynamics. Stay updated with the latest developments in aviation and space industry.',
    keywords = 'aviation news, aerospace news, space technology news, aviation research, aerospace articles, flight technology, space exploration, aircraft news, aviation breaking news, aerospace industry',
    image = '/og-image.jpg',
    url = '',
    type = 'website',
    locale = 'en_US',
    article = null,
    course = null,
    breadcrumb = null,
    faq = null,
    noindex = false
}) => {
    const siteUrl = 'https://aircoredynamics.com';
    const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
    const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

    // Generate structured data based on content type
    const getStructuredData = () => {
        const baseData = {
            "@context": "https://schema.org",
            "@type": type === 'article' ? 'Article' : type === 'course' ? 'Course' : 'WebPage',
            "url": fullUrl,
            "name": title,
            "description": description,
            "image": fullImageUrl,
            "publisher": {
                "@type": "Organization",
                "name": "AircoreDynamics",
                "logo": {
                    "@type": "ImageObject",
                    "url": `${siteUrl}/logo-dark.png`
                }
            }
        };

        // Array to hold multiple structured data objects
        const structuredDataArray = [baseData];

        // Add Breadcrumb schema
        if (breadcrumb && breadcrumb.length > 0) {
            structuredDataArray.push({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": breadcrumb.map((item, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "name": item.name,
                    "item": item.url ? `${siteUrl}${item.url}` : undefined
                }))
            });
        }

        // Add FAQ schema
        if (faq && faq.length > 0) {
            structuredDataArray.push({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faq.map(item => ({
                    "@type": "Question",
                    "name": item.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": item.answer
                    }
                }))
            });
        }

        if (article && type === 'article') {
            const schemaType = article.section === 'News' ? 'NewsArticle' : 
                               article.section === 'Research' ? 'ScholarlyArticle' : 'Article';
                               
            structuredDataArray[0] = {
                ...baseData,
                "@type": schemaType,
                "headline": title,
                "datePublished": article.publishedTime,
                "dateModified": article.modifiedTime || article.publishedTime,
                "author": {
                    "@type": "Person",
                    "name": article.author || "AircoreDynamics Team"
                },
                "articleSection": article.section,
                "keywords": article.tags?.join(', '),
                "wordCount": article.wordCount,
                "timeRequired": article.readingTime ? `PT${article.readingTime}M` : undefined,
                "articleBody": article.excerpt || description,
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": fullUrl
                }
            };
        }

        if (course && type === 'course') {
            structuredDataArray[0] = {
                ...baseData,
                "@type": "Course",
                "name": course.title,
                "description": course.description,
                "provider": {
                    "@type": "Organization",
                    "name": "AircoreDynamics",
                    "sameAs": siteUrl
                },
                "educationalLevel": course.level,
                "hasCourseInstance": {
                    "@type": "CourseInstance",
                    "courseMode": "online",
                    "courseWorkload": course.duration
                },
                "aggregateRating": course.rating ? {
                    "@type": "AggregateRating",
                    "ratingValue": course.rating,
                    "reviewCount": course.reviewCount || 1,
                    "bestRating": "5",
                    "worstRating": "1"
                } : undefined,
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD",
                    "availability": "https://schema.org/InStock",
                    "url": fullUrl
                }
            };
        }

        return structuredDataArray.length === 1 ? structuredDataArray[0] : structuredDataArray;
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <link rel="canonical" href={fullUrl} />

            {/* Hreflang — Turkish content gets tr tag; default is English */}
            {locale === 'tr_TR' ? (
                <>
                    <link rel="alternate" hrefLang="tr" href={fullUrl} />
                    <link rel="alternate" hrefLang="x-default" href={fullUrl} />
                </>
            ) : (
                <>
                    <link rel="alternate" hrefLang="en" href={fullUrl} />
                    <link rel="alternate" hrefLang="x-default" href={fullUrl} />
                </>
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={fullImageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={title} />
            <meta property="og:site_name" content="AircoreDynamics" />
            <meta property="og:locale" content={locale} />

            {/* Article specific tags */}
            {article && (
                <>
                    <meta property="article:published_time" content={article.publishedTime} />
                    <meta property="article:modified_time" content={article.modifiedTime || article.publishedTime} />
                    <meta property="article:author" content={article.author} />
                    <meta property="article:section" content={article.section} />
                    {article.tags && article.tags.map((tag, index) => (
                        <meta key={index} property="article:tag" content={tag} />
                    ))}
                </>
            )}

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullUrl} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={fullImageUrl} />
            <meta name="twitter:image:alt" content={title} />

            {/* Additional SEO */}
            <meta name="robots" content="index, follow" />
            <meta name="language" content={locale === 'tr_TR' ? 'Turkish' : 'English'} />
            <meta name="author" content="AircoreDynamics" />

            {/* Structured Data (JSON-LD) */}
            <script type="application/ld+json">
                {JSON.stringify(getStructuredData())}
            </script>
        </Helmet>
    );
};

export default SEO;
